/**
 * Retry-payment notification tap handlers.
 *
 * Web port of Flutter's `PushNavigationService._showRetryPayPremiumVideoSheet`
 * (`lib/services/push_navigation_service.dart:452-473`) and
 * `_showRetryBoostVideoPaymentSheet` (`:475-496`), reached from the
 * `payPremiumVideoFailed` (`:310-318`) and `payBoostVideoFailed` (`:346-354`)
 * cases, and completed by `_retryPremiumVideoPayment` / `_retryBoostVideoPayment`
 * (`:515-547`). Both sheets are identical apart from their title and the retry
 * call behind the positive button, so the body lives in one private helper.
 *
 * Porting these handlers surfaced two verified Flutter bugs
 * (`conventions.md` → "Handling obvious Flutter bugs"). Both were fixed in BOTH
 * clients on this branch, so web and Dart now agree — there is no open
 * divergence. The notes are kept because they explain why the current shape is
 * the correct one, so nobody "restores parity" by reverting either side:
 *
 *  1. **Boost charge field.** Dart used to pass `mediaInfo.premiumVideoCharge!`
 *     for the BOOST sheet too, but the writer of a `payBoostVideoFailed`
 *     document sets `boostVideoCharge`, not `premiumVideoCharge`
 *     (`cloud_functions/functions/src/functions/notifications/app_notifications.ts:209`;
 *     the premium sibling sets `premiumVideoCharge` at `:167`), and `MediaInfo`
 *     carries the two fields separately (`lib/data/models/media_info.dart:21-22`).
 *     The field is absent on those documents, so the old Dart `!` genuinely threw.
 *     Both clients now read `boostVideoCharge` for the boost case — web via the
 *     inline note in `handlePayBoostVideoFailedTap`, Dart in the
 *     `payBoostVideoFailed` case of `push_navigation_service.dart`. Do NOT
 *     "restore parity" by pointing either one back at `premiumVideoCharge`.
 *  2. **Real retry result.** Dart's `CloudFunctionsService` used to hardcode
 *     `success = true` right after `callable.call` and never inspect the result,
 *     which made the failure toast in `_retryPremiumVideoPayment` /
 *     `_retryBoostVideoPayment` unreachable. Both clients now read the Cloud
 *     Function's real `{data:{success}}` envelope (web: the use cases, Task 1 →
 *     Task 3; Dart: the two retry wrappers in `cloud_functions_service.dart`), so
 *     a `false` genuinely means the retry failed. Unlike
 *     `reviewExclusiveSubscription` (whose `false` means "already cancelled"), a
 *     `false` here is a real failure: it MUST show the error toast and MUST NOT
 *     show the success toast.
 *
 * Caller: `notificationTap/notificationTapRouter.ts` is the only consumer.
 */
import toast from 'react-hot-toast';
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import { retryBoostVideoPaymentUseCase } from '@/domain/usecases/media/retryBoostVideoPaymentUseCase';
import { retryPremiumVideoPaymentUseCase } from '@/domain/usecases/media/retryPremiumVideoPaymentUseCase';
import type { NotificationTapContext } from '@/presentation/feature/inbox/notificationTap/notificationTapContext';

interface RetryPaymentSheetOptions {
  /** Sheet title — premium (`:459`) or boost (`:482`) variant. */
  title: string;
  /** Media the failed payment belongs to (`:313`, `:349`). */
  contentId: string;
  /** Charge shown in the sheet copy, in coins (`:461`, `:484`). */
  coins: number;
  /** The retry use case to run behind the positive button (`:517`, `:534`). */
  retry: (contentId: string) => Promise<boolean>;
  /**
   * Stable QA id for this variant's sheet content root. Required here (unlike
   * the optional `ConfirmSheetConfig.qaId`) because both variants share this
   * body: without it the premium and boost sheets would only be tellable apart
   * by their localized title. Test-only; never affects rendering.
   */
  qaId: string;
}

/**
 * Shared body of both retry sheets (`:452-496` + `:515-547`).
 *
 * `succeeded`-flag + post-response toast shape copied from the existing web
 * precedent `ContentActionsContent.tsx:169-187`, so the inbox and the content
 * actions sheet behave the same way.
 */
const showRetryPaymentSheet = async (
  context: NotificationTapContext,
  { title, contentId, coins, retry, qaId }: RetryPaymentSheetOptions,
): Promise<void> => {
  const { l10n } = context;

  let succeeded = false;
  const response = await ConfirmSheet.show({
    title,
    confirmText: l10n.inbox.notificationTap.retryPaymentDescription.replace(
      '{coins}',
      String(coins),
    ),
    okText: l10n.inbox.notificationTap.retryPayment,
    cancelText: l10n.common.close,
    screenKey: context.screenKey,
    loadingStyle: 'button',
    qaId,
    onConfirm: async () => {
      const ok = await retry(contentId);
      // A `false` is a genuine payment failure here (see file header, note 2).
      if (ok) succeeded = true;
      else toast.error(l10n.inbox.notificationTap.retryPaymentError);
    },
  });

  if (response && succeeded) {
    toast.success(l10n.inbox.notificationTap.retryPaymentSuccess);
  }
};

/**
 * Handles a `payPremiumVideoFailed` notification tap (`:310-318`).
 *
 * Synchronous by design: Flutter does NOT await the sheet (`:312`), so the
 * caller's mark-read (Task 14) fires immediately rather than waiting for the user
 * to answer. The sheet work therefore runs in a fire-and-forget IIFE.
 */
export const handlePayPremiumVideoFailedTap = (context: NotificationTapContext): void => {
  const mediaInfo = context.notification.info.mediaInfo;
  // Flutter null-asserts `mediaInfo!` and `mediaId!` (`:313-314`); every
  // `payPremiumVideoFailed` document is written with them. Defensive no-op
  // instead of a crash on a malformed document — NOT a behaviour change.
  const mediaId = mediaInfo?.mediaId;
  if (!mediaInfo || !mediaId) return;

  void showRetryPaymentSheet(context, {
    title: context.l10n.inbox.notificationTap.retryPremiumVideoTitle,
    contentId: mediaId,
    // `premiumVideoCharge` matches both Flutter (`:314`) and the writer
    // (`app_notifications.ts:167`). `?? 0` only guards a malformed document.
    coins: mediaInfo.premiumVideoCharge ?? 0,
    retry: (id) => retryPremiumVideoPaymentUseCase.execute(id),
    qaId: 'notification-retry-premium-video',
  });
};

/**
 * Handles a `payBoostVideoFailed` notification tap (`:346-354`).
 *
 * Synchronous for the same reason as the premium handler — Flutter does not await
 * the sheet (`:348`).
 */
export const handlePayBoostVideoFailedTap = (context: NotificationTapContext): void => {
  const mediaInfo = context.notification.info.mediaInfo;
  // Same defensive guard as the premium handler (Flutter null-asserts at `:349-350`).
  const mediaId = mediaInfo?.mediaId;
  if (!mediaInfo || !mediaId) return;

  void showRetryPaymentSheet(context, {
    title: context.l10n.inbox.notificationTap.retryBoostVideoTitle,
    contentId: mediaId,
    // Do NOT "fix" this back to `premiumVideoCharge`: the writer of these
    // documents sets `boostVideoCharge` (`app_notifications.ts:209`) and never
    // `premiumVideoCharge`, which is why the old Dart `!` threw on a real
    // document. Dart reads `boostVideoCharge` here too now — both clients agree.
    // See file header, note 1.
    coins: mediaInfo.boostVideoCharge ?? 0,
    retry: (id) => retryBoostVideoPaymentUseCase.execute(id),
    qaId: 'notification-retry-boost-video',
  });
};
